import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Icon,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { ESCURO } from "assets/coresTema";

export default function ModalResumoFila({
  open,
  onClose,
  title,
  blockData,
  color = "info",
  onOpenFicha,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [expandedStatus, setExpandedStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  React.useEffect(() => {
    if (open) {
      setSearchTerm("");
      setExpandedStatus(null);
    }
  }, [open]);

  if (!blockData) return null;

  const itens = blockData.itens || [];
  const porStatus = blockData.porStatus || {};

  // Filtra os itens por Nome ou CPF/CNPJ
  const cleanSearch = searchTerm.toLowerCase().trim();
  const cleanDocSearch = cleanSearch.replace(/[^\d]/g, "");

  const filteredItens = itens.filter((item) => {
    if (!cleanSearch) return true;
    const nome = (item.devedorNome || "").toLowerCase();
    const doc = (item.documento || "").toLowerCase();
    const docDigits = doc.replace(/[^\d]/g, "");

    const matchNome = nome.includes(cleanSearch);
    const matchDoc = doc.includes(cleanSearch) || (cleanDocSearch.length > 0 && docDigits.includes(cleanDocSearch));

    return matchNome || matchDoc;
  });

  // Agrupa os itens filtrados por status
  const itensAgrupados = filteredItens.reduce((acc, item) => {
    const status = item.statusNome || "Sem Status";
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(item);
    return acc;
  }, {});

  const handleAccordionChange = (status) => (event, isExpanded) => {
    setExpandedStatus(isExpanded ? status : null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: darkMode ? ESCURO.cartao : "#ffffff",
          color: darkMode ? "#ffffff" : "#344767",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <MDBox display="flex" alignItems="center" gap={1.5}>
          <Icon color={color} sx={{ fontSize: 32 }}>
            {color === "info" ? "queue" : color === "success" ? "task_alt" : "pending_actions"}
          </Icon>
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold" color={color}>
              Detalhamento de Casos: {title}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Total de {blockData.total || 0} caso(s) alocado(s) na fila de trabalho
            </MDTypography>
          </MDBox>
        </MDBox>
        <IconButton onClick={onClose} sx={{ color: darkMode ? "#fff" : "#333" }}>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
        <MDBox mb={2.5}>
          <MDInput
            fullWidth
            size="small"
            placeholder="Pesquisar por Nome do Devedor ou CPF / CNPJ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: darkMode ? "#aaa" : "#666" }}>search</Icon>
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ color: darkMode ? "#aaa" : "#666" }}>
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </MDBox>

        {Object.keys(itensAgrupados).length === 0 ? (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="body2" color="text">
              {searchTerm ? "Nenhum caso encontrado para a pesquisa informada." : "Nenhum caso encontrado nesta categoria."}
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={2}>
            {Object.entries(itensAgrupados).map(([statusNome, listaItens]) => (
              <Accordion
                key={statusNome}
                expanded={expandedStatus === null || expandedStatus === statusNome}
                onChange={handleAccordionChange(statusNome)}
                sx={{
                  borderRadius: "10px !important",
                  background: darkMode ? ESCURO.painel : "#f8f9fa",
                  border: "1px solid",
                  borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandMoreIcon={<ExpandMoreIcon sx={{ color: darkMode ? "#fff" : "#333" }} />}
                  sx={{
                    px: 2,
                    py: 1,
                    minHeight: "48px !important",
                    "& .MuiAccordionSummary-content": { my: 0 },
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                    <MDTypography variant="button" fontWeight="bold" color="text">
                      {statusNome}
                    </MDTypography>
                    <MDBox
                      px={1.5}
                      py={0.3}
                      borderRadius="12px"
                      bgcolor={darkMode ? ESCURO.campo : "#e2e8f0"}
                    >
                      <MDTypography variant="caption" fontWeight="bold" color={color}>
                        {listaItens.length} caso(s)
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
                  <TableContainer component={Paper} elevation={0} sx={{ background: "transparent", width: "100%", boxShadow: "none" }}>
                    <Table size="small" sx={{ width: "100%" }}>
                      <MDBox component="thead">
                        <TableRow>
                          <DataTableHeadCell width="40%" sorted={false}>Devedor</DataTableHeadCell>
                          <DataTableHeadCell width="25%" sorted={false}>CPF / CNPJ</DataTableHeadCell>
                          <DataTableHeadCell width="20%" align="right" sorted={false}>Saldo (R$)</DataTableHeadCell>
                          <DataTableHeadCell width="15%" align="center" sorted={false}>Ação</DataTableHeadCell>
                        </TableRow>
                      </MDBox>
                      <TableBody>
                        {listaItens.map((item) => (
                          <TableRow key={item.filaId} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                            <TableCell width="40%" sx={{ color: darkMode ? "#fff" : "#333", fontSize: "0.85rem", py: 1.5, verticalAlign: 'top' }}>
                              <MDTypography variant="button" fontWeight="medium" color="text">
                                {item.devedorNome || "Não informado"}
                              </MDTypography>
                            </TableCell>
                            <TableCell width="25%" sx={{ color: darkMode ? "#ddd" : "#555", fontSize: "0.85rem", py: 1.5, verticalAlign: 'top' }}>
                              {item.documento || "-"}
                            </TableCell>
                            <TableCell width="20%" align="right" sx={{ color: darkMode ? "#fff" : "#333", fontWeight: "bold", fontSize: "0.85rem", py: 1.5, verticalAlign: 'top' }}>
                              {item.saldo ? item.saldo.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "R$ 0,00"}
                            </TableCell>
                            <TableCell width="15%" align="center" sx={{ py: 1.5, verticalAlign: 'top' }}>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => onOpenFicha(item.devedorId)}
                                sx={{ py: 0.4, px: 1.5, fontSize: "0.75rem", whiteSpace: "nowrap" }}
                              >
                                <Icon sx={{ fontSize: "1rem", mr: 0.5 }}>folder_open</Icon>
                                Abrir Ficha
                              </MDButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </MDBox>
        )}
      </DialogContent>
    </Dialog>
  );
}
